import React, {ReactElement, useContext, useEffect, useState} from 'react';
import "../../assets/css/Dentobook.css";
import {
    InvoiceResponse,
    Patient
} from "../../api-client";
import {InvoiceDocumentTypeEnum} from "../../types/calendarEnums";
import {notification, Select, Space, Table, Tooltip} from "antd";
import {FormattedMessage, useIntl} from "react-intl";
import {CaretDownFilled, CheckCircleOutlined, EditOutlined, FileAddOutlined, PrinterOutlined} from "@ant-design/icons";
import '../../assets/css/PatientFinances.css';
// @ts-ignore
import {ColumnsType} from "antd/es/table";
import dayjs from "dayjs";
import PatientProformaUpdateModal from "./PatientProformaUpdateModal";
import PatientInvoiceVoidModal from "./PatientInvoiceVoidModal";
import PatientInvoiceGenerateModal from "./PatientInvoiceGenerateModal";
import {DentobookApplicationContext} from "../../context/DentobookApplicationContext";
import {getInvoicesApi} from "../../api/apiInstances";
import logger from "../../utils/logger";

interface PatientFinancesProformaInvoicesProps {
    patient: Patient;
}

interface ProformaDataType {
    key: string | undefined;
    number: string | undefined;
    date: string | undefined;
    maturity: string | undefined;
    price_value: string | undefined;
    payment_type: string | undefined;
    status: ReactElement | undefined;
}

const PatientFinancesProformaInvoices = ({patient}: PatientFinancesProformaInvoicesProps) => {
    const {userOrganization} = useContext(DentobookApplicationContext);

    const currency = userOrganization?.currency ?? 'EUR';

    const [patientInvoices, setPatientInvoices] = useState<InvoiceResponse[]>([]);
    const [updateProformaModalOpen, setUpdateProformaModalOpen] = useState<boolean>(false);
    const [selectedInvoice, setSelectedInvoice] = useState<InvoiceResponse | null>(null);
    const [totalPaid, setTotalPaid] = useState<number>(0);
    const [totalUnpaid, setTotalUnpaid] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [isVoidModalOpen, setIsVoidModalOpen] = useState<boolean>(false);
    const [isInvoiceGenerateOpen, setIsInvoiceGenerateOpen] = useState<boolean>(false);

    const intl = useIntl();

    const invoicesApi = getInvoicesApi();

    useEffect(() => {
        if (!patient.id) {
            return;
        }

        loadData();
    }, []);

    const loadData = () => {
        invoicesApi.fetchInvoicesByPatientId(patient.id!)
            .then(res => {
                const filteredInvoices = res.data.filter((invoice: InvoiceResponse) => invoice.documentType === InvoiceDocumentTypeEnum.PROFORMA_INVOICE);
                setPatientInvoices(filteredInvoices);

                const paidInvoices = filteredInvoices.filter(invoice => invoice.status === 'PAID');
                const unpaidInvoices = filteredInvoices.filter(invoice => invoice.status === 'UNPAID_PROFORMA');

                const totalPaidSum = paidInvoices.reduce((acc, invoice) => acc + (invoice.total || 0), 0);
                const totalUnpaidSum = unpaidInvoices.reduce((acc, invoice) => acc + (invoice.total || 0), 0);

                setTotalPaid(totalPaidSum);
                setTotalUnpaid(totalUnpaidSum);
            })
            .catch(err => logger.log(err));
    };

    const handleStatusChange = (invoiceId: string, value: string) => {
        const updatedStatus = value === '0' ? 'UNPAID_PROFORMA' : 'CANCELLED_PROFORMA';
        const invoiceToUpdate = patientInvoices.find(invoice => invoice.id === invoiceId);

        if (invoiceToUpdate) {
            const updatedInvoice = {...invoiceToUpdate, status: updatedStatus};
            if (updatedInvoice.id != null) {
                if (updatedStatus === 'CANCELLED_PROFORMA') {
                    setIsVoidModalOpen(true);
                    setSelectedInvoice(invoiceToUpdate)
                } else {
                    invoicesApi.updateInvoice(updatedInvoice.id!, updatedInvoice as InvoiceResponse)
                        .then(() => {
                            loadData();
                        })
                        .catch(err => logger.error(err));
                }
            }
        }
    };

    const handleEditClick = (invoice: InvoiceResponse) => {
        setSelectedInvoice(invoice);
        setUpdateProformaModalOpen(true);
    };

    const handleConvertProforma = async (invoice: InvoiceResponse) => {
        try {
            if (invoice.id != null) {
                await invoicesApi.convertProformaToInvoice(invoice.id);
                notification.success({
                    message: 'Фактурата е създадена успешно.',
                    description: '',
                    placement: 'topRight',
                    duration: 3,
                    style: {
                        backgroundColor: '#f6ffed',
                        border: '1px solid #b7eb8f',
                        borderRadius: '4px',
                        padding: '16px',
                        display: 'flex',
                        alignItems: 'center',
                    },
                    icon: <CheckCircleOutlined style={{color: '#52c41a'}}/>
                });
            }
        } catch (error) {
            logger.error(error);

        } finally {
            loadData();
        }
    }

    const generatePdf = async (invoice: InvoiceResponse) => {
        setLoading(true);
        try {
            // Download PDF with proper authentication
            const pdfResponse = await invoicesApi.generateInvoicePdf(invoice.id!, { responseType: 'blob' });
            const blob = new Blob([pdfResponse.data], {type: 'application/pdf'});
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Proforma_${invoice.invoiceNumber}.pdf`;
            link.click();
            setTimeout(() => window.URL.revokeObjectURL(url), 100);
        } catch (error) {
            logger.error(error);
        } finally {
            setLoading(false);
        }
    };

    const cashPaymentMethod = intl.formatMessage({id: 'cash_payment_type'});
    const cardPaymentMethod = intl.formatMessage({id: 'card_payment_type'});
    const bankTransferPaymentMethod = intl.formatMessage({id: 'bank_transfer'});

    const data: ProformaDataType[] = patientInvoices.map(invoice => {
        let selectClass = '';
        let selectValue = '';
        let selectDisabled = false;
        switch (invoice.status) {
            case 'CANCELLED_PROFORMA':
                selectClass = 'dentobook-finances-select-canceled';
                selectValue = '2';
                selectDisabled = true;
                break;
            case 'UNPAID_PROFORMA':
                selectClass = 'dentobook-finances-select-unpaid';
                selectValue = '0';

                break;
        }

        let paymentMethod = '';
        switch (invoice.paymentMethod) {
            case "BANK_TRANSFER":
                paymentMethod = bankTransferPaymentMethod;
                break;
            case "CARD":
                paymentMethod = cardPaymentMethod;
                break;
            default:
                paymentMethod = cashPaymentMethod;
                break;
        }

        return {
            key: invoice.id,
            number: invoice.invoiceNumber,
            date: dayjs(invoice.date).utc().format('DD.MM.YYYY'),
            maturity: dayjs(invoice.paymentDueDate).utc().format('DD.MM.YYYY'),
            price_value: (invoice.total ?? 0).toFixed(2).toString(), // Ensure price_value is a string
            payment_type: paymentMethod,
            status: <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                <Select
                    dropdownStyle={{width: 130}}
                    key={invoice.id}
                    className={selectClass}
                    suffixIcon={<CaretDownFilled/>}
                    value={selectValue}
                    onChange={(value) => handleStatusChange(invoice.id!, value)}
                    disabled={selectDisabled}
                >
                    <Select.Option value="2"><FormattedMessage id="canceled_invoice"/></Select.Option>
                    <Select.Option value="0"><FormattedMessage id="unpaid_invoice"/></Select.Option>
                </Select>
                <Space size={"large"}>
                    <Tooltip title={intl.formatMessage({id: 'generate_invoice_tooltip'})}>
                        <FileAddOutlined style={{fontSize: 22}} onClick={() => {
                            setSelectedInvoice(invoice);
                            setIsInvoiceGenerateOpen(true);
                        }}/>
                    </Tooltip>
                    <Tooltip title={intl.formatMessage({id: 'edit_tooltip'})}>
                        <EditOutlined style={{fontSize: 22}} onClick={() => handleEditClick(invoice)}/>
                    </Tooltip>
                    <Tooltip title={intl.formatMessage({id: 'print_tooltip'})}>
                        <PrinterOutlined onClick={() => generatePdf(invoice)} style={{fontSize: 22}}/>
                    </Tooltip>
                </Space>
            </div>
        };
    });

    const proformasColumns: ColumnsType<ProformaDataType> = [
        {
            title: '№:',
            dataIndex: 'number',
            key: 'number',
            width: '12%'
        },
        {
            title: useIntl().formatMessage({id: 'date'}),
            dataIndex: 'date',
            key: 'date',
            width: '12%'
        },
        {
            title: useIntl().formatMessage({id: 'maturity'}),
            dataIndex: 'maturity',
            key: 'maturity',
            width: '12%'
        },
        {
            title: useIntl().formatMessage({id: 'price_value'}),
            dataIndex: 'price_value',
            key: 'price_value',
            width: '12%'
        },
        {
            title: useIntl().formatMessage({id: 'payment_type'}),
            dataIndex: 'payment_type',
            key: 'payment_type',
            width: '12%'
        },
        {
            title: useIntl().formatMessage({id: 'status'}),
            dataIndex: 'status',
            key: 'status'
        },
    ];

    if (loading) {
        return (
            <h1><FormattedMessage id="loading"/></h1>
        );
    }

    const tableFooter = () => (
        <div className="dentobook-operations-table-footer">
            <span className="font-weight-700"><FormattedMessage
                id="paid_obligations"/>: {totalPaid ? totalPaid.toFixed(2) + ' ' + currency : '00.00 ' + currency}</span>
            <span className="font-weight-700"><FormattedMessage
                id="obligations"/>: {totalUnpaid ? totalUnpaid.toFixed(2) + ' ' + currency : '00.00 ' + currency}</span>
        </div>
    );

    return (
        <>
            {selectedInvoice && (
                <>
                    <PatientProformaUpdateModal
                        patient={patient}
                        isModalOpen={updateProformaModalOpen}
                        setIsModalOpen={(isOpen: boolean | ((prevState: boolean) => boolean)) => {
                            setUpdateProformaModalOpen(isOpen);
                            if (!isOpen) loadData(); // Call loadData when modal is closed
                        }}
                        proforma={selectedInvoice}
                    />

                    <PatientInvoiceVoidModal proforma={true}
                                             isModalOpen={isVoidModalOpen}
                                             setIsModalOpen={setIsVoidModalOpen}
                                             invoice={selectedInvoice}
                                             loadData={loadData}
                    />

                    <PatientInvoiceGenerateModal invoice={selectedInvoice}
                                                 isModalOpen={isInvoiceGenerateOpen}
                                                 setIsModalOpen={setIsInvoiceGenerateOpen}
                                                 convertProforma={handleConvertProforma}
                    />
                </>
            )}
            <h3 className="font-family-roboto font-weight-500 font-24"><FormattedMessage id="proforma_invoices"/></h3>
            <div style={{marginTop: -28}} className="dentobook-content dentobook-scrollable-table mb-32">
                <Table
                    footer={tableFooter}
                    className={"teeth-table pt-2"}
                    columns={proformasColumns}
                    dataSource={data}
                    pagination={false}/>
            </div>
        </>
    );
};

export default PatientFinancesProformaInvoices;